import {useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {Box, Typography} from '@mui/material';

import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {useRejectionReasons} from '../../../../../../hooks/useRejectionReasons';
import {greenColors, orangeColors} from '../../../../../../layouts/theme/designSystem';
import {MatchStatus} from '../../../../../../types/MatchStatus/MatchStatus';
import {MatchData, MatchRejectionInfo, Rejection} from '../../../../../../types/shared-recruiting-types';
import {humanizeSnakeCase} from '../../../../../../utils/humanizeSnakeCase';

interface Props {
  match?: MatchData;
  status?: string;
  rejectionInfo?: MatchRejectionInfo | null;
}

/** API sends `company_employee` or `applicant` for `rejected_by`. */
function isTalentRejection(rejectedBy: string): boolean {
  return rejectedBy.toLowerCase() === 'applicant';
}

function labelForReasonKey(reasons: Rejection[] | undefined, key: string, lang: 'en' | 'de'): string {
  const found = reasons?.find((r) => r.rejectionReasonKey === key);
  return found?.rejectionReasonMessage[lang] ?? humanizeSnakeCase(key);
}

export const RejectionInfoBox = ({match, status, rejectionInfo}: Props) => {
  const lang = useContext(LanguageContext);
  const {t} = useTranslation('translation', {keyPrefix: 'matchDetailsPage.rejectionInfo'});

  const info = rejectionInfo ?? match?.rejectionInfo;
  const resolvedStatus = status ?? match?.status;
  const isRejected = resolvedStatus === MatchStatus.Rejected;
  const shouldFetchReasons = isRejected && !!info;
  const rejectionType = info && isTalentRejection(info.rejectedBy) ? 'applicant' : 'company';

  const {data: reasons} = useRejectionReasons(rejectionType, {enabled: shouldFetchReasons});

  const categoryLabel = useMemo(
    () => (info ? labelForReasonKey(reasons, info.rejectionReasonKey, lang) : ''),
    [info, reasons, lang]
  );

  if (!isRejected || !info) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 3,
        mb: 1,
        p: 2,
        borderRadius: '12px',
        backgroundColor: orangeColors[7],
        borderLeft: `4px solid ${greenColors[700]}`,
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5}}>
        <InfoOutlinedIcon sx={{color: greenColors[700], fontSize: 22, mt: 0.25}} />
        <Typography variant='h4' component='h2' sx={{color: greenColors[900]}}>
          {isTalentRejection(info.rejectedBy) ? t('rejectedByTalent') : t('rejectedByEmployer')}
        </Typography>
      </Box>
      <Typography variant='body2' sx={{color: greenColors[100], mb: 0.5}}>
        {t('category')}
      </Typography>
      <Typography variant='body1' sx={{color: greenColors[900], mb: 1.5}}>
        {categoryLabel}
      </Typography>
      <Typography variant='body2' sx={{color: greenColors[100], mb: 0.5}}>
        {t('details')}
      </Typography>
      <Typography variant='body1' sx={{color: greenColors[900], whiteSpace: 'pre-wrap'}}>
        {info.rejectionReasonMessage}
      </Typography>
      {info.talentMessage?.trim() && (
        <>
          <Typography variant='body2' sx={{color: greenColors[100], mb: 0.5, mt: 1.5}}>
            {t('rejectionText')}
          </Typography>
          <Typography variant='body1' sx={{color: greenColors[900], whiteSpace: 'pre-wrap'}}>
            {info.talentMessage}
          </Typography>
        </>
      )}
    </Box>
  );
};
